/**
 * Where a company got the address in `prospect.email` (PREC-4262). Stored per company in
 * `prospect_email_meta.emailSource` (VARCHAR(16)), one row per (prospect, company), written after
 * the prospect's save from what the API's one write path, `setProspectEmail`, returns. NULL means
 * unknown (an address written before PREC-4262 that the backfill could not attribute, or a writer
 * that does not know).
 *
 * - `SIGNALIST` – Signalist's own finder or scrapers found it for this company.
 * - `UPLOAD`    – it came in with a file this company uploaded (CSV import).
 * - `CRM_SYNC`  – a CRM sync wrote it.
 * - `MANUAL`    – a user of this company typed or confirmed it in the app (Edit contact info, the
 *                 Outbox composer, confirming a pending address, a one-off add).
 * - `EXTENSION` – a user added it from the Chrome extension.
 *
 * The prospect row is shared by every company whose lists hold the person, so the source is not
 * stored on it: a company is shown only its own row, and only while that row describes the
 * prospect's current address.
 */
export enum EmailSource {
	SIGNALIST = "SIGNALIST",
	UPLOAD = "UPLOAD",
	CRM_SYNC = "CRM_SYNC",
	MANUAL = "MANUAL",
	EXTENSION = "EXTENSION",
}

export const EMAIL_SOURCES = Object.values(EmailSource);

/**
 * The split the email hover shows (PREC-4262): Signalist's own addresses on one side, every address
 * a customer brought (upload, CRM, typed, extension) on the other; `null` when the source is unknown.
 * The sub-type is for analytics only.
 */
export type EmailSourceGroup = "SIGNALIST" | "USER_PROVIDED";

export function emailSourceGroup(source?: string | null): EmailSourceGroup | null {
	if (!source || !(EMAIL_SOURCES as string[]).includes(source)) {
		return null;
	}

	return source === EmailSource.SIGNALIST ? "SIGNALIST" : "USER_PROVIDED";
}

/** What the lead's latest email-step send ended as (PREC-4262). */
export const LAST_EMAIL_OUTCOMES = ["SENT", "BOUNCED"] as const;

export type LastEmailOutcomeKind = (typeof LAST_EMAIL_OUTCOMES)[number];

/** The lead's latest email-step send: whether it went out or bounced, and when. */
export interface LastEmailOutcome {
	outcome: LastEmailOutcomeKind;
	at: string | Date;
}
